/* eslint-disable @typescript-eslint/ban-ts-comment */
import 'react-perfect-scrollbar/dist/css/styles.css';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { memo, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RootState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { dateLabel } from 'utils/groupByDate';
import moment from 'moment';
import _ from 'lodash';
import api from 'services/api';
import { ModalContext } from 'context/ModalContext';
import { MODAL_ANIMATIONS, MODAL_TYPES } from 'components/Modal/constants';
import { setShowRedline } from 'store/chatsSlice';
import { isMobile } from 'react-device-detect';

import CommentItem from './_components/CommentItem';
import { handleChatList } from 'store/chatsSlice';
import { removeComment } from 'api/commentAPI';
import AddForm from './_components/AddForm';

import { useLocation } from 'react-router-dom';

interface IChatView {
  channelId: string;
  navigateVideoPage: any;
  videoId: string;
  videoHashId: string;
  selectedVideo: any;
  selectedChannel: any;
  setSelectedChannel: any;
  navigateChannel: any;
}

function ChatView({
  channelId,
  navigateVideoPage,
  videoId,
  videoHashId,
  selectedVideo,
  selectedChannel,
  setSelectedChannel,
  navigateChannel
}: IChatView) {
  // const chatList = useSelector((state: RootState) => state.chats[videoHashId]);

  const chatState = useSelector((state: RootState) => state.chats); // ChatsState
  // const activeHashId = useSelector((state: RootState) => state.chats.activeHashId); // string
  const chatList = chatState[videoHashId]; // SingleChatState
  const [newChat, setNewChat] = useState('');
  const bottomRef = useRef(document.createElement('div'));
  const channelList = useSelector((state: RootState) => state.channels.channelList);
  const { showModal } = useContext(ModalContext);
  const dispatch = useDispatch();
  const [editingId, setEditingId] = useState<null | string>(null);
  const userData = useSelector((state: RootState) => state.user.user);
  const [loading, setLoading] = useState<boolean>(true);

  const location = useLocation();
  const videoMatch = location.pathname.match(/\/video\/([^/]+)/);
  const activeVideoPageId = videoMatch ? videoMatch[1] : undefined;
  const [hoveredId, setHoveredId] = useState(null as any);

  useLayoutEffect(() => {
    const API_URL =
      channelId !== 'home'
        ? `/api/videos/${videoHashId}/${channelId}/messages/`
        : `/api/videos/${videoHashId}/messages/`;
    api.get(API_URL).then((res) =>
      dispatch(
        handleChatList({
          hash_id: videoHashId,
          channel_hash_id: channelId,
          messages: res.data.messages
        })
      )
    );
    setLoading(false);
  }, [videoHashId, channelId]);

  const handleEdit = (id: string) => {
    if (editingId !== null && editingId !== id) {
      showModal(MODAL_TYPES.edit_summary, {
        animation: MODAL_ANIMATIONS.fade,
        channelId,
        setEditingId,
        editingId: id,
        itemType: 'comment'
      });
    } else {
      setEditingId(id);
    }
  };

  const handleDelete = async (commentId: string) => {
    const confirmDelete = async () => {
      await removeComment(commentId);
    };

    showModal(MODAL_TYPES.remove_summary, {
      animation: MODAL_ANIMATIONS.fade,
      modalMessage: {
        title: 'Deleting Comment',
        text: 'You are about to to delete a comment.  Click "Confirm to delete" button.'
      },
      onConfirm: confirmDelete
    });
  };

  const createNewChat = (content: string, time: number) => {
    return api
      .post('/api/videos/message/', {
        channel_id: selectedChannel.hash_id,
        video_id: selectedVideo.hash_id,
        content: content,
        time: time
      })
      .then(() => {
        setNewChat('');
        if (bottomRef.current) {
          bottomRef.current.scrollIntoView();
        }
      });
  };

  // function handleChatKeyPress(event: any) {
  //   if (event.key === 'Enter') {
  //     event.preventDefault();
  //     createNewChat();
  //     setNewChat('');
  //   }
  // }

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          dispatch(setShowRedline({ hash_id: videoHashId, show: false }));
        } else {
          dispatch(setShowRedline({ hash_id: videoHashId, show: true }));
        }
      });
    });
    observer.observe(bottomRef.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  const groupMessagesByDates = useMemo(() => {
    // const validMessages = (chatList.messages ?? []).filter(
    //   (msg: any): msg is { timestamp: number } => typeof msg?.timestamp === 'number'
    // );
    const messages = chatList?.messages;
    return _.groupBy(messages, dateLabel);
  }, [chatList]);

  // useLayoutEffect(() => {
  //   bottomRef.current?.scrollIntoView();
  // }, [chatList.messages]);

  if (loading) {
    return <p></p>;
  }

  return (
    <div className={`absolute w-full flex flex-col flex-1 top-0 right-0 left-0 bottom-0 overflow-y-hidden`}>
      <PerfectScrollbar
        style={{
          maxHeight: isMobile ? 'calc(100% - 100px)' : activeVideoPageId ? 'calc(100% - 150px)' : 'calc(100% - 40px)'
        }}
        options={{ suppressScrollY: false, suppressScrollX: true }}
        className="flex flex-row  w-[calc(100%-16px)]"
      >
        <div className="relative mt-auto w-full">
          {Object.keys(groupMessagesByDates).map((group: any, groupIdx: number) => (
            <div
              key={moment(new Date(groupMessagesByDates[group][0].timestamp * 1000)).format('YYYY-MM-DD')}
              id={moment(new Date(groupMessagesByDates[group][0].timestamp * 1000)).format('YYYY-MM-DD')}
              className="relative"
            >
              <div
                className="bg-white sticky top-0 z-10 w-fit mx-auto rounded-lg mt-[6px]"
                id={moment(new Date(groupMessagesByDates[group][0].timestamp * 1000)).format('YYYY-MM-DD') + '-sticky'}
              >
                <div className="pl-4 pr-4 h-8 leading-8 text-[13px] font-lato font-bold cursor-pointer w-fit flex relative items-center mx-auto z-10 bg-black bg-opacity-5 hover:bg-opacity-10 rounded-lg">
                  {group}
                </div>
              </div>

              {groupMessagesByDates[group].map((item: any, index: number) => (
                <div key={channelId + '-' + item.hash_id} id={channelId + '-' + item.hash_id}>
                  {chatList.newlyAddedChats.length &&
                  chatList.messages.length -
                    Array(groupIdx)
                      .fill('')
                      .reduce((acc, cur, idx) => {
                        return acc + groupMessagesByDates[Object.keys(groupMessagesByDates)[idx]]?.length;
                      }, index) ===
                    chatList.newlyAddedChats.length &&
                  chatList.newlyAddedChats[0].hash_id === item.hash_id ? (
                    <div
                      className={`w-full flex h-0 pointer-events-none ${
                        index === 0 ? 'absolute w-full top-[18px]' : ''
                      }`}
                    >
                      <hr
                        className="bg-horizon h-[1px] flex-1 border-t-0 opacity-50 duration-[10ms] transition-opacity ease-in delay-300"
                        aria-hidden="true"
                      />
                      <span className="text-red-raspberry_red text-[13px] font-bold py-0 px-1 -mt-[10px] mr-[15px]">
                        New
                      </span>
                    </div>
                  ) : (
                    <div
                      className={`border-t absolute w-full top-[18px] border-iconColor border-opacity-20 border-b-0 ${
                        index === 0 ? 'block' : 'hidden'
                      }`}
                    ></div>
                  )}
                  {/* <Message
                    user={item.message_user}
                    content={item.content}
                    videoShotIdx={groupIdx}
                    navigateLink={navigateVideoPage}
                    timestamp={1000 * item.timestamp}
                    displayAvatar={
                      index === 0 ||
                      (index && chatList.messages[index - 1].message_user.user_id !== item.message_user.user_id)
                    }
                  /> */}
                  <CommentItem
                    key={item?.hash_id}
                    videoHashId={videoHashId}
                    hashId={item?.hash_id}
                    displayAvatar={
                      index === 0 ||
                      (index && chatList.messages[index - 1]?.message_user?.user_id !== item?.message_user?.user_id)
                    }
                    user={{ avatar: item?.message_user?.avatar, display_name: item?.message_user?.display_name }}
                    content={item.content}
                    navigateLink={navigateVideoPage}
                    time={item.start_time}
                    userData={userData}
                    handleEdit={() => {
                      handleEdit(item.hash_id);
                    }}
                    isEditing={editingId === item.hash_id}
                    handleCancelEdit={() => {
                      setEditingId(null);
                    }}
                    onDelete={() => {
                      handleDelete(item.hash_id);
                    }}
                    hoveredId={hoveredId}
                    handleHoveredId={() => {
                      setHoveredId(item.hash_id);
                    }}
                  />
                </div>
              ))}
            </div>
          ))}
          <div ref={bottomRef} className="h-1" />
        </div>
      </PerfectScrollbar>

      <div className="text-[13px] text-[#111]">
        {channelId !== 'home' ? (
          <>
            {activeVideoPageId && (
              <AddForm
                channelId={channelId}
                onSubmit={createNewChat}
                textinputPlaceholder={`Write a commment for #${selectedChannel?.name}`}
                selected={0}
              ></AddForm>
            )}
          </>
        ) : (
          <div className="mb-4 pb-1 border-t border-[#e5e6e7] rounded-md w-full">
            {selectedVideo?.channels?.length === 0 ? (
              <p className="px-3 py-[6px]">
                <span
                  className="text-[#1664A3] cursor-pointer"
                  onClick={() =>
                    showModal(MODAL_TYPES.share_video, {
                      animation: MODAL_ANIMATIONS.unfold,
                      videoId: videoId,
                      videoHashId: videoHashId,
                      videoName: name
                    })
                  }
                >
                  Share this video{' '}
                </span>
                to a channel to chat
              </p>
            ) : (
              <p className="px-3 py-[6px]">
                Select a channel to chat: <br />
                {[...(selectedVideo?.channels ?? [])]
                  .sort((channelA: { is_archived: any }, ChannelB: { is_archived: any }) => {
                    if (channelA.is_archived === ChannelB.is_archived) {
                      return 0;
                    }
                    if (channelA.is_archived) {
                      return 1;
                    } else {
                      return -1;
                    }
                  })
                  .map((item: any, idx: number) => (
                    <span
                      className={` ${
                        item.is_archived ? '' : 'text-[#1664A3] font-bold hover:underline cursor-pointer '
                      }`}
                      key={item.channel.name}
                      onClick={() => {
                        if (item.is_archived) {
                          return;
                        }
                        setSelectedChannel(
                          channelList.find((channel: any) => channel.hash_id === item.channel.hash_id)
                        );
                        navigateChannel(item.channel.hash_id);
                      }}
                    >
                      {idx !== 0 && ', '}
                      {`#${item.channel.name}${item.is_archived ? ' (archived)' : ''}`}
                    </span>
                  ))}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(ChatView);
